import * as angular from 'angular';
import { getManualInputProperties } from '../../widgets/manualInputs/manualInputsContextual/getManualInputProperties';
import {
    AllowLogbookEntries,
    Decimals,
    GroupGuid,
    IsBoundToGroup,
    IsDependentGroupsIncluded,
    LaunchTask
} from '../../common/helpers/contextual';

import {
    ComponentDefinition,
    ContextualField,
    Dashboard,
    Dictionary,
    Group,
    GroupConfigMode,
    IErrorReporting,
    IdentifierType,
    Miniature,
    PropertyType
} from '../../models/index';
import { IWidget } from '../../widgets';
import { SpotManualInputsWidgetDefinition } from '../../widgets/spotManualInputs/spotManualInputs.definition';

class GroupValidationContextualController implements angular.IComponentController {
    errors: { [widgetType: string]: IErrorReporting[] };
    extraProperties: Dictionary<ContextualField[]> = {};
    formController: angular.IFormController;
    groupChildren: Group[];
    referenceGroups: Group[];
    showComment: boolean;
    watcherDispose: () => void;
    widgetsMiniature: Miniature[];

    readonly allGroups: Group[];
    readonly cancel: () => angular.IPromise<void>;
    readonly dashboards: Dashboard[]; //TODO check type
    readonly group: Group;
    readonly manualGroups: Group[];
    readonly manualSpotGroups: Group[];
    readonly miniatureList: Miniature[];
    readonly save: any; //TODO check type
    readonly selectedItem: IWidget;

    private _validationGroups: Group[];

    static $inject = ['$scope'];

    constructor(
        private _$scope: angular.IScope
    ) {
    }

    $onInit() {
        const widgetsMiniature = (this.widgetsMiniature || []).concat([
            {
                title: 'Group validation',
                type: 'ValidatedValueSelection',
                highlights: ['Validated identifiers of selected group', 'Users can manually validate and optionally approve the data'],
                icon: 'fa-check-square-o',
                sortingOrder: -200,
            } as Miniature
        ]);
        this.extraProperties['LogbookTable'] = [angular.copy(LaunchTask)];
        this.extraProperties['ManualInputs'] = getManualInputProperties(this.group, this.manualGroups ?? []);
        this.extraProperties['ValidatedValueSelection'] = [
            Object.assign({}, GroupGuid, {
                modelName: 'group',
                required: false,
                extraData: { groups: this.groupChildren }
            }),
            Object.assign({}, IsBoundToGroup, { modelName: 'isBound', hide: this.group.configMode !== 0 }),
            {
                icon: 'fa-object-group',
                label: 'Only allow selection of a complete column',
                modelName: 'isColumnMode',
                placeholder: 'Column mode',
                propertyName: 'isColumnModeProperty',
                propertyType: PropertyType.Boolean,
                required: false
            },
            {
                icon: 'fa-comment-o',
                label: 'Short comment',
                modelName: 'showShortComment',
                placeholder: 'Short comment',
                propertyName: 'showShortCommentProperty',
                propertyType: PropertyType.Boolean,
                required: false
            },
            {
                icon: 'fa-balance-scale',
                label: 'Unit',
                modelName: 'showUnit',
                placeholder: 'Unit',
                propertyName: 'showUnitProperty',
                propertyType: PropertyType.Boolean,
                required: false
            },
            {
                icon: 'fa-tag',
                label: 'Tag name',
                modelName: 'showTagName',
                placeholder: 'Tag name',
                propertyName: 'showTagNameProperty',
                propertyType: PropertyType.Boolean,
                required: false
            },
            {
                icon: 'fa-neuter',
                iconText: 'V',
                label: 'Value type',
                modelName: 'showValueType',
                placeholder: 'Value type',
                propertyName: 'showValueTypeProperty',
                propertyType: PropertyType.Boolean,
                required: false,
            },
            {
                icon: 'fa-user-plus',
                label: 'User comment',
                modelName: 'showComment',
                placeholder: 'User comment',
                propertyName: 'showCommentProperty',
                propertyType: PropertyType.Boolean,
                required: false,
                onChange: (viewValue: boolean) => {
                    this.selectedItem.configuration.hideComment = !viewValue;
                }
            },
            {
                icon: 'fa-check',
                label: 'Show quality',
                modelName: 'showQuality',
                placeholder: 'Show quality',
                propertyName: 'showQualityProperty',
                propertyType: PropertyType.Boolean,
                required: false,
            },
            Object.assign({}, Decimals),
            Object.assign({}, LaunchTask),
            Object.assign({}, IsDependentGroupsIncluded),
            {
                icon: 'fa-thumbs-o-up',
                label: 'Enable approval',
                modelName: 'enableApproval',
                placeholder: 'Enable approval',
                propertyName: 'enableApprovalProperty',
                propertyType: PropertyType.Boolean,
                required: false,
            },
            Object.assign({}, AllowLogbookEntries)
        ];
        const errorsByWidget: { [widgetType: string]: IErrorReporting[] } = {};
        Object.keys(this.extraProperties).forEach(key => {
            errorsByWidget[key] =
                this.extraProperties[key].map(c => {
                    const { errorName, messages, propertyName } = c;
                    return {
                        errorName,
                        messages,
                        propertyName
                    };
                });
        });
        this.errors = errorsByWidget;
        if (this.manualGroups && this.manualGroups.length && !widgetsMiniature.filter(w => w?.title === 'Manual inputs').length) {
            widgetsMiniature.push({
                title: 'Manual inputs',
                type: 'ManualInputs',
                highlights: ['Manual input identifiers of selected group', 'Users can enter and optionally approve data'],
                icon: 'fa-columns',
                sortingOrder: -100
            } as Miniature);
        }
        if (this.manualSpotGroups && this.manualSpotGroups.length && !widgetsMiniature.filter(w => w?.title === SpotManualInputsWidgetDefinition.title).length) {
            widgetsMiniature.push(Object.assign({ }, SpotManualInputsWidgetDefinition));
        }
        this.widgetsMiniature = widgetsMiniature;

        this._$scope.$watch(() => this.showComment, () => {
            if (this.selectedItem) {
                this.selectedItem.configuration.hideComment = !this.showComment;
            }
        });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        this.init();

        if (onChangesObj['selectedItem']) {
            this.updateSelectedItem();
        }
        let widgetsMiniature = (this.widgetsMiniature || []).map(d => d);
        if (onChangesObj['miniatureList']) {
            widgetsMiniature = widgetsMiniature
                .concat(this.miniatureList);
        }
        if (onChangesObj['manualGroups']) {
            if (this.manualGroups && this.manualGroups.length && !widgetsMiniature.filter(w => w?.title === 'Manual inputs').length) {
                widgetsMiniature.push({
                    title: 'Manual inputs',
                    type: 'ManualInputs',
                    highlights: ['Manual input identifiers of selected group', 'Users can enter and optionally approve data'],
                    icon: 'fa-columns',
                    sortingOrder: -100
                } as Miniature);
            }
        }
        if (onChangesObj['manualSpotGroups']) {
            if (this.manualSpotGroups && this.manualSpotGroups.length && !widgetsMiniature.filter(w => w?.title === SpotManualInputsWidgetDefinition.title).length) {
                widgetsMiniature.push(Object.assign({ }, SpotManualInputsWidgetDefinition));
            }
        }
        this.widgetsMiniature = widgetsMiniature;
    }

    private updateSelectedItem() {
        if (this.watcherDispose) {
            this.watcherDispose();
        }
        if (this.selectedItem) {
            this.showComment = !this.selectedItem.configuration.hideComment;
        }
        if (this.selectedItem && this.group
            && this.group.configMode === GroupConfigMode.ByType) {
            this.watcherDispose = this._$scope.$watch(
                'this.selectedItem.configuration.referenceGroup',
                () => this.updateGroupChildren()
            );
        }
    }

    private init() {
        if (this.group && this.allGroups) {
            this._validationGroups = this.allGroups.filter(g => g.identifierCollection.filter(i => i.type === IdentifierType.Validated).length > 0);
            if (this.group.configMode === GroupConfigMode.ByGroup) {
                // #46920 : allow to select any group
                this.groupChildren = this.allGroups;
            } else {
                this.referenceGroups = this.getReferenceGroups(this.allGroups, this.group);
                this.groupChildren = this._validationGroups;
            }
            if (this.extraProperties && this.extraProperties['ValidatedValueSelection']) {
                const props = this.extraProperties['ValidatedValueSelection'].filter(d => d.model === 'group');
                if (props.length) {
                    props[0].extraData.groups = this.groupChildren;
                }
            }
        }
    }

    private getReferenceGroups(groups: Group[], currentGroup: Group): Group[] {
        let referenceGroups = groups.filter(g => g.type === currentGroup.type);
        const children = referenceGroups.map(r => groups.filter(g => g.absoluteName.indexOf(r.absoluteName) === 0 && g.absoluteName !== r.absoluteName));
        referenceGroups = referenceGroups
            .concat(...children)
            .sort((a, b) => a.absoluteName > b.absoluteName ? 1 : -1);

        if (currentGroup.configMode === GroupConfigMode.ByType) {
            referenceGroups = referenceGroups.filter(g => g.type === currentGroup.type);
        }

        return referenceGroups;
    }

    private updateGroupChildren() {
        this.groupChildren = this._validationGroups;
    }
}

export const wtGroupValidationContextual: ComponentDefinition = {
    componentId: 'wtGroupValidationContextual',
    controller: GroupValidationContextualController,
    template: require('./groupValidationContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        allGroups: '<',
        cancel: '&?',
        dashboards: '<',
        formController: '=',
        group: '<',
        manualGroups: '<',
        manualSpotGroups: '<',
        miniatureList: '<',
        save: '&',
        selectedItem: '<',
    }
};